// janus-engine/traps/fire.ts — Lane TRAPS (2026-09-28, owner: port trap FIRING to the browser; "all of these things should be in front end
// logic based on JS/TS logic"). A fired trap's payload — the host inventory row is the caster, the interactor the one target — resolved in
// the browser; the trap commit records it (options/game/actions/trap_truth_commit_inc.php `fire`), it never rolls.
//
// PHP (ft_trap_fire → janus(trigger: 'trap_ability')) wrote only the fixed-DC row and applied FULL damage every time — the victim never
// rolled its contest, so `contesting_roll_successful_for_half_damage` and the contest-failed statuses were dead layers (oracle
// tools/probes/traps_fire_oracle.php). The FRONT-END RULE (the payload's own layers):
//   · the trap's side is its fixed DC — the host's Trap DC (ft_effective_ability_dc's trap branch: inventory_inspection_dc ?? 10), logged
//     as the ability_dc row (no draw);
//   · the victim rolls its skill_roll_contestee skill against it (the ability lane's selectSkillForContestingRoll + skillRollArr: luck,
//     unlucky, ascension, Fortunate all included); the trap wins a tie;
//   · trap wins → the contest-failed statuses (post_effect_conditions) and FULL damage; victim wins → HALF damage when the payload says so;
//   · the damage (and the victim's damage-taken reactions) through the ability lane's damageTypeRoll — the RX executor dispatches them.
// Every census payload (5, 2026-09-28) has exactly this shape; anything else is UNMODELLED (reported + refused by the commit, never rolled).
import { selectSkillForContestingRoll } from '../ability/select-skill-for-contesting-roll.js';
import { skillRollArr } from '../ability/skill-roll.js';
import { damageTypeRoll } from '../ability/rolls/damage-type-roll.js';
import { postEffectConditions } from '../ability/post-effect-conditions.js';
import { posCoord } from '../../shared/array-types';
import { HandBack, Refusal, ecChanges, ecLean, insertRoll, type JanusCtx, type JanusReaders, type LogRow, type Row, type StatusRow, type StatusStackChange } from '../ability/context.js';
import { frameCopyOnRead } from '../ability/frame-snapshot.js';
import type { SpawnRowPlan } from '../spawn/row-plan.js';
import { payloadDryRunRollSource } from '../ability/environmental-payload.js';
import { recordingRollSource, type RecordedDraw, type RollSource } from '../actions/roll-source.js';
import { type JanusInput } from '../ability/janus.js';
import { consentPending, TRIGGER_CONSENT_REASON } from '../ability/trigger-consent.js';
import { planTrapLifecycle, trapArmed, type TrapLifecycle } from './lifecycle.js';

export const TRAP_TRIGGER = 'trap_ability' as const;
const CONTESTING_ROLL = 'AD80D31D-898B-66CE-0894-1335D11B1529';
/** The layers a census payload authors (+ item meta). Anything else → unmodelled. */
const TRAP_PAYLOAD_LAYERS = new Set(['ability_dc', 'contesting_roll_successful_for_half_damage', 'damage_type', 'dice', 'die', 'roll_type',
  'skill_roll_contestee', 'trap_classification', 'trap_reset', 'trap_trigger', 'status_effect_applied_to_target_after_contesting_roll_failed',
  'unenchantable', 'cost_gp', 'range']);

const up = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const clone = (value: Row): Row => frameCopyOnRead(value, new Map(), { catalogs: 'share' }).encounter;

export interface TrapFireInput {
  encounter: Row;
  /** The page's member rows keyed upper-case encounter_creature_id (cloned, never mutated). */
  members: ReadonlyMap<string, Row>;
  /** The trap's host inventory row (the caster). */
  host: Row;
  hostId: string;
  /** The interactor the payload targets. */
  victimId: string;
  /** The mounted trap_ability that fires. */
  abilityId: string;
  read: JanusReaders;
  rollPrivate: number;
}
export interface TrapFireResolved {
  kind: 'rolled';
  rows: LogRow[];
  draws: RecordedDraw[];
  dc: number;
  /** The victim's contest total, and whether the TRAP won (tie → trap). */
  contest: { total: number; trapWon: boolean };
  /** Column changes per creature (after values) and the values before (the commit's CAS). */
  ec: Record<string, Record<string, unknown>>;
  ecBefore: Record<string, Record<string, unknown>>;
  statusesAdded: StatusRow[];
  statusesRemoved: JanusCtx['statusesRemoved'];
  statusStacks: StatusStackChange[];
  spawnRows?: SpawnRowPlan;
  teleportOffers?: JanusCtx['teleportOffers'];
  charges: Array<{ holder: string; item_id: string; before: number; charges: number }>;
  pc: Array<{ item_id: string; skill_id: string; column: string; before: number; after: number }>;
  /** Compatibility field for older callers; actual deaths are emitted as typed rows. */
  deathTail: Array<{ target: string; class: string; massiveLog: boolean; afterRow: number }>;
  lifecycle: TrapLifecycle;
}
export type TrapFireVerdict = TrapFireResolved | { kind: 'no_fire' } | { kind: 'unmodelled'; reason: string };

/** ft_effective_ability_dc()'s trap branch: the host's Trap DC (inventory_inspection_dc), else 10. */
export function trapHostDc(host: Row): number {
  const raw = (host.inventory as Row | undefined)?.inventory_inspection_dc ?? host.inventory_inspection_dc;
  const n = Number(raw);
  return raw !== null && raw !== undefined && raw !== '' && Number.isFinite(n) ? Math.trunc(n) : 10;
}

function context(input: TrapFireInput, roll: RollSource): JanusCtx {
  return {
    ...frameCopyOnRead(input.encounter, input.members, { catalogs: 'share' }), originals: input.members,
    read: input.read, roll, rows: [], statusesAdded: [], statusesRemoved: [], chargesChanged: [], inventoryChanged: [],
    statusConnectedId: null, rollPrivate: input.rollPrivate, trigger: TRAP_TRIGGER,
    deathTail: [], teleportOffers: [],
  };
}

function runFire(ctx: JanusCtx, input: TrapFireInput, contestExtreme?: 'min' | 'max'): { dc: number; total: number; trapWon: boolean } {
  const abilityId = up(input.abilityId);
  const ability = ctx.read.definition(abilityId);
  if (!ability) throw new HandBack('trap_fire:payload-not-held');
  if (String((ability.items as Row | undefined)?.item_type ?? '') !== 'trap_ability') throw new HandBack('trap_fire:item-type');
  for (const key of Object.keys((ability.item_layer_types as Row | undefined) ?? {}))
    if (!TRAP_PAYLOAD_LAYERS.has(key)) throw new HandBack(`trap_fire:layer:${key}`);
  if (up(ctx.read.value(ability, 'roll_type')) !== CONTESTING_ROLL) throw new HandBack('trap_fire:roll-type');
  if (!ctx.read.has(ability, 'skill_roll_contestee') || !ctx.read.has(ability, 'damage_type')) throw new HandBack('trap_fire:shape');
  const victim = ctx.members.get(up(input.victimId));
  if (!victim) throw new HandBack('trap_fire:victim-not-held');
  if (Number(ecLean(victim, 'encounter_creature_dead') ?? 0) === 1) throw new HandBack('trap_fire:victim-dead');
  const host = clone(input.host);
  const dc = trapHostDc(host);
  const janusInput: JanusInput = { casterId: up(input.hostId), targetIds: [up(input.victimId)], abilityId, rawDiceMod: 0, rawDiceAdvDisStacks: 0,
    useAbilityVariable1: null, useAbilityVariable2: null };
  // The trap's fixed DC — its row, no draw (contesting_roll.php's fixed-DC branch).
  insertRoll(ctx, { roll_type_1: CONTESTING_ROLL, roll_type_2: 'ability_dc', roll_item_id: abilityId, by: host, roll_result: dc });
  // The victim's contest: its contestee skill against the DC (a trap has no facing — no directional cover).
  const skill = selectSkillForContestingRoll(ctx, victim, ctx.read.entries(ability, 'skill_roll_contestee'));
  const payloadRoll = ctx.roll;
  if (contestExtreme) ctx.roll = payloadDryRunRollSource(contestExtreme);
  let total: number;
  try { total = skillRollArr(ctx, janusInput, victim, skill, host, dc); }
  finally { ctx.roll = payloadRoll; }
  const trapWon = dc >= total;
  if (trapWon) postEffectConditions(ctx, janusInput, ability, host, victim, true);
  const half = !trapWon && ctx.read.has(ability, 'contesting_roll_successful_for_half_damage');
  if (trapWon || half) damageTypeRoll(ctx, janusInput, ability, host, victim, half);
  return { dc, total, trapWon };
}

/** The shared boundary and typed-row savers persist the complete actual outcome. */
function commitShape(ctx: JanusCtx): string | null {
  if (ctx.inventoryChanged.length) return 'trap_fire:inventory';
  if ((ctx.concentrationBreaks ?? []).length) return 'trap_fire:concentration-tail';
  return null;
}

const DRY_RUNS = ['max', 'min', 'alt-hi', 'alt-lo'] as const;
// A high victim contest can halve all-max damage. Prove both contest extremes independently
// of the damage tape, including a lost contest followed by an all-max lethal payload.
const PREVIEWS = [...DRY_RUNS.map(pattern => ({ pattern, contest: undefined as 'min' | 'max' | undefined })),
  ...(['min', 'max'] as const).flatMap(contest => (['min', 'max'] as const).map(pattern => ({ pattern, contest })))];

/** Check payload capabilities without deciding death from synthetic damage. */
export function preflightTrapFire(input: TrapFireInput): { kind: 'ready'; lifecycle: TrapLifecycle } | Exclude<TrapFireVerdict, TrapFireResolved> {
  if (!trapArmed(input.host, input.read.statuses)) return { kind: 'no_fire' };
  let lifecycle: TrapLifecycle;
  try {
    const ability = input.read.definition(up(input.abilityId));
    if (!ability) return { kind: 'unmodelled', reason: 'trap-lifecycle:definition-not-held' };
    lifecycle = planTrapLifecycle(input.host,input.encounter,input.members,ability,input.read);
  } catch (error) {
    if (error instanceof HandBack) return { kind: 'unmodelled', reason: error.reason };
    throw error;
  }
  for (const { pattern, contest } of PREVIEWS) {
    const dry = context(input, payloadDryRunRollSource(pattern));
    dry.dryRun = true;
    try { runFire(dry, input, contest); }
    catch (error) {
      if (error instanceof HandBack) return { kind: 'unmodelled', reason: `preflight:${pattern}:${error.reason}` };
      if (error instanceof Refusal) return { kind: 'unmodelled', reason: `preflight:${pattern}:refusal` };
      return { kind: 'unmodelled', reason: `preflight:${pattern}:exception:${String((error as Error)?.message ?? error).slice(0, 80)}` };
    }
    const why = commitShape(dry);
    if (why) return { kind: 'unmodelled', reason: `preflight:${pattern}:${why}` };
  }
  // OWNER RULING 2026-10-02 (lane A12 — AB1's cross-lane ask): the dry runs met an optional trigger (a reaction) of a PLAYER-controlled victim
  // nobody has answered → the page asks its controller (≤ 5 s) and resolves again with the answer final; never a real die first.
  if (consentPending(input.read)) return { kind: 'unmodelled', reason: `preflight:${TRIGGER_CONSENT_REASON}` };
  return { kind: 'ready', lifecycle };
}

/** Roll once after the shared state/capability checks. */
export function resolveTrapFire(input: TrapFireInput, roll: RollSource): TrapFireVerdict {
  const checked = preflightTrapFire(input);
  if (checked.kind !== 'ready') return checked;
  const { lifecycle } = checked;
  const recording = recordingRollSource(roll);
  const ctx = context(input, recording);
  let result: ReturnType<typeof runFire>;
  try { result = runFire(ctx, input); }
  catch (error) {
    if (error instanceof HandBack) return { kind: 'unmodelled', reason: error.reason };
    if (error instanceof Refusal) return { kind: 'unmodelled', reason: error.message };
    throw error;
  }
  if (consentPending(ctx.read)) return { kind: 'unmodelled', reason: `outcome:${TRIGGER_CONSENT_REASON}` };
  const { dc, total, trapWon } = result;
  const why = commitShape(ctx);
  if (why) return { kind: 'unmodelled', reason: `trap_fire:preflight-missed:${why}` };
  const after = ecChanges(ctx);
  const ec: Record<string, Record<string, unknown>> = {}, ecBefore: Record<string, Record<string, unknown>> = {};
  for (const [id, cols] of Object.entries(after)) for (const [col, value] of Object.entries(cols)) {
    const original = input.members.get(up(id));
    const before = original ? col === 'x_pos' || col === 'y_pos' || col === 'z_pos' ? posCoord(original, col) ?? 0 : ecLean(original, col) : null;
    if (String(before ?? '') === String(value ?? '')) continue;
    (ec[up(id)] ??= {})[col] = value; (ecBefore[up(id)] ??= {})[col] = before;
  }
  return { kind: 'rolled', rows: ctx.rows, draws: recording.draws, dc, contest: { total, trapWon }, ec, ecBefore,
    statusesAdded: ctx.statusesAdded.map(st => ({ ...st, status_id: st.status_id ?? input.read.uuid() })), statusesRemoved: ctx.statusesRemoved.map(st => ({ ...st })),
    statusStacks: (ctx.statusStacks ?? []).map(st => ({ ...st })), ...(ctx.spawnRows ? { spawnRows: ctx.spawnRows } : {}),
    ...(ctx.teleportOffers?.length ? { teleportOffers: ctx.teleportOffers } : {}),
    charges: ctx.chargesChanged.map(c => ({ ...c })),
    pc: (ctx.playerCharactersChanged ?? []).map(c => ({ ...c })),
    deathTail: (ctx.deathTail ?? []).map(d => ({ target: up(d.target), class: String(d.class), massiveLog: !!d.massiveLog, afterRow: Number(d.afterRow) })), lifecycle };
}
